/* ============================================================================
   Banda de precio. Una celda —una presentación, un canal, una geografía, un
   mes— dibujada como la nube de la que salió, no como un número suelto.

   Existe porque el precio base se defiende en un acta. La mediana sola no se
   defiende: en la misma central, el mismo día y con la misma presentación, el
   jitomate saladette de Iztapalapa del 16 de mayo de 2026 fue $42, $34 y $20.
   Aquí van pegados al número las tres cosas que lo sostienen —cuántas
   observaciones, en cuántas entidades, qué tan ancha es la nube— y el corchete
   del intervalo, que es lo que dice cuánto de esa mediana es muestra y cuánto
   es mercado.

   Lo que la banda NO hace: no promedia, no interpola y no compara canales por
   su cuenta. El caret de anaquel y el tick del INEGI son marcas que el que
   llama decide poner; la banda los dibuja donde caen y no calcula ninguna
   diferencia con ellos. El del INEGI está para que la distancia contra la otra
   fuente se VEA en vez de explicarse, no para restarse.

   Con `grade === 'solo-rango'` la regla de la mediana no se dibuja: la celda no
   pasó la compuerta y una regla gruesa afirmaría un punto que no representa a
   nadie. Ésa es toda la diferencia visual y basta (§4.3 de la especificación).

   La tabla espejo reusa las clases `chart__table*` de `chart.css` a propósito:
   la regla de impresión que la abre a la fuerza (`base.css:276-278`) apunta a
   `details.chart__table > .chart__table-scroll`. Con clases propias, el PDF se
   llevaría la banda sin sus cifras, que es justo lo que la tabla vino a evitar.
   ========================================================================== */

import { useMemo } from 'react'
import { linearScale, niceExtent } from './primitives'
import { formatPeriodLong, integer } from '../lib/format'
import { useSize } from '../lib/motion'
import { RETAIL_CHANNEL_LABEL, type BandGrade, type PriceCell } from '../data/schema'
import './chart.css'
import './priceBand.css'

export interface PriceBandMarker {
  value: number
  /** Rótulo de texto del ancla. Obligatorio: el color nunca carga el sentido solo. */
  label: string
  /** `anaquel` dibuja caret naranja; `referencia`, tick fino de tinta atenuada. */
  tone: 'anaquel' | 'referencia'
}

export interface PriceBandProps {
  cell: PriceCell
  /**
   * Rótulo de la presentación: «Frijol negro, granel, 1 kg».
   *
   * Nombra la celda en el `caption` de la tabla espejo y en el `aria-label` de
   * su región. Sin él la tabla es anónima —«Mediana (p50) $26.00» sin decir de
   * qué producto—, y quien navega por tablas con lector de pantalla la
   * encuentra suelta, fuera del contexto visual que la explica. `BarList` y
   * `LineChart` ya nombran lo suyo; ésta era la única que no.
   *
   * El canal y el mes NO se piden: viven en la celda (`cell.channel`,
   * `cell.period`) y leerlos de ahí impide que el rótulo discrepe del dato que
   * la tabla imprime.
   */
  label: string
  markers?: PriceBandMarker[]
  /**
   * Intervalo de la mediana a dibujar bajo la barra.
   *
   * La celda ya trae el suyo en `ciLo`/`ciHi`; este prop existe para poder
   * pasar otro. Si no llega, se usa el de la celda: dibujar el intervalo propio
   * de la celda nunca es incorrecto, y omitirlo por descuido borra la única
   * cifra que dice cuánto de la mediana es muestra.
   */
  ci?: { lo: number; hi: number } | null
  format: (v: number) => string
  /** «pesos por kilo». Va a la tabla, a la leyenda y al nombre accesible. */
  unitLabel: string
  grade: BandGrade
  /** Qué umbral falló, en prosa, cuando `grade === 'solo-rango'`. */
  gradeReason: string | null
  footnote: string
}

/* Bajo 420 px se caen los rótulos de p10/p90 y quedan p25/p75, y el alto pasa
   de 96 a 128 px (§4.10). Los dos números salen de sumar las tres franjas. */
const COMPACT_W = 420
const BAR_H = 22
const CAJA = {
  ancha: { head: 34, foot: 40, inset: 34 },
  angosta: { head: 50, foot: 56, inset: 24 },
} as const

/**
 * Ancho mínimo de un tramo dibujable.
 *
 * Un tramo de ancho cero pasa de verdad: el azúcar en bolsa de 1 kg en anaquel
 * (mayo de 2026) trae 68 observaciones y las cinco están en $19.00 — p10, p25,
 * p50, p75 y p90 idénticos. Son 259 de las 9 941 celdas del archivo de detalle
 * (2.6 %) con p10 = p90 y 589 (5.9 %) con p25 = p75. Un `<rect>` de ancho 0 no
 * pinta nada y la ausencia se lee como «no hay dato», que es falso: hay 68
 * observaciones y todas coinciden. Se dibuja una marca centrada en el valor y
 * el pie lo dice con todas sus letras.
 *
 * 13 px y no 3: el halo de la regla de la mediana mide 7 px y un tramo
 * colapsado siempre cae justo debajo de ella —si p25 = p75, entonces p50 vale
 * lo mismo—, así que cualquier marca más angosta que el halo desaparece detrás
 * de él y volvemos a la barra invisible. Con 13 quedan 3 px de banda visibles a
 * cada lado de la regla. El piso de ancho tiene precedente en la casa:
 * `BarList.tsx:204` dibuja `Math.max(2, …)` por la misma razón.
 */
const MARCA_MIN = 13

/** Ancho aproximado de un carácter en `--t-caption-size` con cifras tabulares. */
const CHAR_CAPTION = 6.6
/** Ídem en `--t-micro-size` en versalitas, medido sobre el rótulo de la banda. */
const CHAR_MICRO_ALTA = 8
/** Ídem en `--t-title2-size`, que es la cifra de la mediana. */
const CHAR_TITLE2 = 11.5
/** Aire mínimo entre dos rótulos de cuantil antes de considerar que chocan. */
const AIRE = 8
/** Medio ancho del halo de la regla de la mediana, más aire. */
const REGLA_AIRE = 5

/** El rótulo del tramo p25–p75, textual de §4.3. Va en la banda y en la leyenda. */
const ROTULO_RANGO = 'rango de negociación'

interface Tramo {
  x: number
  width: number
}

/**
 * Tramo horizontal con piso de ancho, centrado sobre el valor cuando colapsa.
 *
 * `dentroDe` acota el tramo interior al exterior: con p25 = p75 = p10 —pasa en
 * 402 de las 39 764 combinaciones de celda × ancho que probé— la marca centrada
 * se salía 6.5 px por la izquierda de la nube que la contiene, y un rango de
 * negociación pintado fuera de su propia distribución es un dibujo que miente.
 */
function tramo(x0: number, x1: number, dentroDe?: Tramo): Tramo {
  const w = x1 - x0
  const ancho = Math.max(w, MARCA_MIN)
  let x = w >= MARCA_MIN ? x0 : x0 - (MARCA_MIN - w) / 2
  if (dentroDe) {
    x = Math.min(Math.max(x, dentroDe.x), dentroDe.x + dentroDe.width - ancho)
  }
  return { x, width: ancho }
}

type Ancla = 'start' | 'middle' | 'end'

/** Ancla el texto para que no se salga de la caja del SVG. Copia la regla de
 *  los extremos del eje X de `LineChart.tsx:604-605`. */
function anclar(px: number, half: number, min: number, max: number): { px: number; ancla: Ancla } {
  if (px - half < min) return { px: min, ancla: 'start' }
  if (px + half > max) return { px: max, ancla: 'end' }
  return { px, ancla: 'middle' }
}

function capitalizar(texto: string): string {
  return `${texto.charAt(0).toUpperCase()}${texto.slice(1)}`
}

export function PriceBand({
  cell,
  label,
  markers,
  ci,
  format,
  unitLabel,
  grade,
  gradeReason,
  footnote,
}: PriceBandProps) {
  const [boxRef, size] = useSize<HTMLDivElement>()

  const width = size.width || 640
  const compact = width < COMPACT_W
  const caja = compact ? CAJA.angosta : CAJA.ancha
  const height = caja.head + BAR_H + caja.foot
  const innerW = Math.max(1, width - caja.inset * 2)

  const barY = caja.head
  const barBottom = barY + BAR_H

  const marcas = useMemo(
    () => (markers ?? []).filter((m) => Number.isFinite(m.value)),
    [markers],
  )

  /* El intervalo dibujado. `null` es ausencia y se explica en el pie; nunca se
     rellena con la mediana ni con los cuartiles para «tener algo que pintar». */
  const intervalo = useMemo(() => {
    if (ci) return ci
    if (cell.ciLo !== null && cell.ciHi !== null) return { lo: cell.ciLo, hi: cell.ciHi }
    return null
  }, [ci, cell.ciLo, cell.ciHi])

  /* El dominio incluye las marcas: el anaquel de la tortilla ($14.40) cae fuera
     de la nube de la central ($20.00–$29.00) y recortarlo escondería justo la
     distancia que la marca vino a mostrar. `niceExtent` ya resuelve el caso
     degenerado —min === max le suma un 5 % a cada lado— así que la escala nunca
     se queda con un span de cero. */
  const extent = useMemo(
    () =>
      niceExtent(
        [
          cell.p10,
          cell.p25,
          cell.p50,
          cell.p75,
          cell.p90,
          ...(intervalo ? [intervalo.lo, intervalo.hi] : []),
          ...marcas.map((m) => m.value),
        ],
        { pad: 0.1 },
      ),
    [cell, intervalo, marcas],
  )

  const x = useMemo(
    () => linearScale([extent.min, extent.max], [0, innerW]),
    [extent, innerW],
  )

  const sinDispersion = cell.p90 === cell.p10
  const rangoPuntual = !sinDispersion && cell.p75 === cell.p25

  const externo = tramo(x(cell.p10), x(cell.p90))
  const interno = tramo(x(cell.p25), x(cell.p75), externo)

  const minX = -caja.inset
  const maxX = innerW + caja.inset

  /* Rótulos de cuantil bajo la barra. Prioridad p25/p75: delimitan el rango de
     negociación y son los dos que la §4.10 conserva en pantalla angosta. El que
     choca con uno ya puesto se cae entero —apilarlo o inclinarlo lo despega de
     su posición y se lee como ruido, misma regla que las etiquetas de fin de
     curva de `LineChart.tsx:277-282`—. La tabla espejo los tiene todos. */
  const rotulos = useMemo(() => {
    const candidatos = compact
      ? [
          { id: 'p25', valor: cell.p25 },
          { id: 'p75', valor: cell.p75 },
        ]
      : [
          { id: 'p25', valor: cell.p25 },
          { id: 'p75', valor: cell.p75 },
          { id: 'p10', valor: cell.p10 },
          { id: 'p90', valor: cell.p90 },
        ]
    const puestos: { id: string; px: number; half: number; texto: string }[] = []
    for (const c of candidatos) {
      const texto = format(c.valor)
      const half = (texto.length * CHAR_CAPTION) / 2
      const px = x(c.valor)
      if (puestos.some((p) => Math.abs(p.px - px) < p.half + half + AIRE)) continue
      puestos.push({ id: c.id, px, half, texto })
    }
    return puestos
  }, [cell, compact, format, x])

  const publicable = grade === 'publicable'

  /* «rango de negociación» dentro del tramo p25–p75 cuando cabe; si no, se
     queda sólo en la leyenda, que siempre está.
     No se centra en el tramo: como p25 ≤ p50 ≤ p75 por construcción, la regla
     de la mediana siempre cae dentro y partía el rótulo por la mitad —medido en
     la tortilla, la regla entraba entre «NEGO» y «CIACIÓN»—. Se escribe en el
     más ancho de los dos lados de la regla. */
  const rotuloRangoX = useMemo(() => {
    if (compact) return null
    const ancho = ROTULO_RANGO.length * CHAR_MICRO_ALTA
    const lados = publicable
      ? [
          { x0: interno.x, x1: x(cell.p50) - REGLA_AIRE },
          { x0: x(cell.p50) + REGLA_AIRE, x1: interno.x + interno.width },
        ]
      : [{ x0: interno.x, x1: interno.x + interno.width }]
    let mejor: { x0: number; x1: number } | null = null
    for (const lado of lados) {
      const libre = lado.x1 - lado.x0
      if (libre < ancho + 20) continue
      if (mejor === null || libre > mejor.x1 - mejor.x0) mejor = lado
    }
    return mejor === null ? null : (mejor.x0 + mejor.x1) / 2
  }, [compact, publicable, interno, x, cell.p50])

  const textoP50 = format(cell.p50)
  const p50Ancla = anclar(x(cell.p50), (textoP50.length * CHAR_TITLE2) / 2, minX, maxX)

  const ariaLabel = useMemo(() => {
    const marcasFrase = marcas.map((m) => `${m.label}: ${format(m.value)}`).join('. ')
    return [
      `Banda de precio en ${unitLabel}.`,
      publicable
        ? `Precio base ${textoP50}.`
        : 'Sin precio base publicable: la celda no pasó la compuerta.',
      `Rango de negociación de ${format(cell.p25)} a ${format(cell.p75)}.`,
      `El 10 % más barato en ${format(cell.p10)} y el 10 % más caro en ${format(cell.p90)}.`,
      intervalo
        ? `Intervalo de confianza del 95 % de la mediana, de ${format(intervalo.lo)} a ${format(intervalo.hi)}.`
        : 'Sin intervalo de confianza para la mediana.',
      marcasFrase ? `${marcasFrase}.` : '',
      `${integer(cell.n)} observaciones en ${integer(cell.states)} entidades y ${integer(cell.outlets)} puntos de venta.`,
      'La tabla de datos completa está bajo la gráfica.',
    ]
      .filter((s) => s !== '')
      .join(' ')
  }, [marcas, format, unitLabel, publicable, textoP50, cell, intervalo])

  return (
    <figure className="priceband">
      {/* §5.3. La regla de la mediana no se dibuja y este párrafo ocupa su
          lugar: el rango que sí se puede convocar, y el umbral que falló.
          `unitLabel` entra como aposición y no como «el kilo»: el denominador
          vive en la referencia, no en la celda, y deducirlo partiendo la cadena
          de la unidad es la clase de suposición que esta pantalla no admite. */}
      {!publicable ? (
        <p className="priceband__sin-punto t-callout">
          <strong>Sin precio base publicable.</strong> Entre{' '}
          <strong className="tabular">{format(cell.p25)}</strong> y{' '}
          <strong className="tabular">{format(cell.p75)}</strong>, {unitLabel}.
          {gradeReason !== null ? ` ${capitalizar(gradeReason)}` : ''}
        </p>
      ) : null}

      <div className="priceband__plot" ref={boxRef} role="img" aria-label={ariaLabel}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="priceband__svg"
          aria-hidden="true"
          focusable="false"
        >
          <g transform={`translate(${caja.inset} 0)`}>
            {/* p10–p90: la nube completa. */}
            <rect
              x={externo.x}
              y={barY}
              width={externo.width}
              height={BAR_H}
              rx={4}
              fill="var(--seq-200)"
            />

            {/* p25–p75: el rango de negociación. */}
            <rect
              x={interno.x}
              y={barY}
              width={interno.width}
              height={BAR_H}
              rx={4}
              fill="var(--seq-500)"
            />

            {rotuloRangoX !== null ? (
              <text
                x={rotuloRangoX}
                y={barY + BAR_H / 2}
                className="priceband__band-label"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {ROTULO_RANGO}
              </text>
            ) : null}

            {/* Tick y rótulo de cada cuantil que cupo. */}
            {rotulos.map((r) => {
              const a = anclar(r.px, r.half, minX, maxX)
              return (
                <g key={r.id}>
                  <line
                    x1={Math.round(r.px) + 0.5}
                    x2={Math.round(r.px) + 0.5}
                    y1={barBottom}
                    y2={barBottom + 4}
                    className="priceband__tick"
                  />
                  <text
                    x={a.px}
                    y={barBottom + (compact ? 34 : 30)}
                    className="priceband__quantile tabular"
                    textAnchor={a.ancla}
                  >
                    {r.texto}
                  </text>
                </g>
              )
            })}

            {/* Corchete del IC 95 %, bajo la barra. */}
            {intervalo ? (
              <path
                d={`M${x(intervalo.lo)} ${barBottom + (compact ? 10 : 8)}L${x(intervalo.lo)} ${
                  barBottom + (compact ? 16 : 14)
                }L${x(intervalo.hi)} ${barBottom + (compact ? 16 : 14)}L${x(intervalo.hi)} ${
                  barBottom + (compact ? 10 : 8)
                }`}
                className="priceband__ci"
              />
            ) : null}

            {/* Marcas. El halo de 2 px en color de superficie es el mismo recurso
                que los marcadores de `LineChart`: sin él, el tick de tinta
                atenuada desaparece sobre el azul del tramo p25–p75. */}
            {marcas.map((m) => (
              <g key={`${m.tone}-${m.label}`}>
                <line
                  x1={x(m.value)}
                  x2={x(m.value)}
                  y1={barY - 6}
                  y2={barBottom + 6}
                  className="priceband__marker-halo"
                />
                <line
                  x1={x(m.value)}
                  x2={x(m.value)}
                  y1={barY - 6}
                  y2={barBottom + 6}
                  className={
                    m.tone === 'anaquel' ? 'priceband__marker-stem' : 'priceband__marker-tick'
                  }
                />
                {/* El caret lleva tallo hasta la barra a propósito: el precio de
                    anaquel cae fuera de la nube con frecuencia —la tortilla, a
                    $14.40 contra una central de $20.00 a $29.00— y un triángulo
                    suelto sobre el vacío se lee como un error de dibujo, no como
                    «el anaquel está acá afuera». */}
                {m.tone === 'anaquel' ? (
                  <path
                    d={`M${x(m.value) - 5} ${barY - 12}L${x(m.value) + 5} ${barY - 12}L${x(m.value)} ${barY - 4}Z`}
                    fill="var(--div-up)"
                    stroke="var(--surface-1)"
                    strokeWidth={1.5}
                    strokeLinejoin="round"
                  />
                ) : null}
              </g>
            ))}

            {/* Regla de la mediana y su cifra. Sólo con la celda publicable. */}
            {publicable ? (
              <>
                <rect
                  x={x(cell.p50) - 3.5}
                  y={barY - 7}
                  width={7}
                  height={BAR_H + 14}
                  rx={3.5}
                  fill="var(--surface-1)"
                />
                <rect
                  x={x(cell.p50) - 1.5}
                  y={barY - 6}
                  width={3}
                  height={BAR_H + 12}
                  rx={1.5}
                  fill="var(--ink-1)"
                />
                {/* Línea base 17 px por encima de la barra: el caret de anaquel
                    ocupa de −12 a −4 y las cifras no tienen descendentes, así
                    que quedan 5 px libres aunque el anaquel caiga justo sobre la
                    mediana. */}
                <text
                  x={p50Ancla.px}
                  y={caja.head - 17}
                  className="priceband__figure tabular"
                  textAnchor={p50Ancla.ancla}
                >
                  {textoP50}
                </text>
              </>
            ) : null}
          </g>
        </svg>
      </div>

      {/* Leyenda: el rótulo de texto de cada ancla. Va `aria-hidden` porque el
          `aria-label` del gráfico ya la dicta entera y la tabla la repite; lo
          que no puede faltar es la versión VISIBLE, que es la que impide que el
          color cargue el sentido solo. */}
      <ul className="priceband__legend" aria-hidden="true">
        {publicable ? (
          <li className="priceband__legend-item t-caption">
            <span className="priceband__key priceband__key--rule" />
            precio base <span className="tabular">{textoP50}</span>
          </li>
        ) : null}
        <li className="priceband__legend-item t-caption">
          <span className="priceband__key priceband__key--inner" />
          {ROTULO_RANGO}{' '}
          <span className="tabular">
            {format(cell.p25)} – {format(cell.p75)}
          </span>
        </li>
        <li className="priceband__legend-item t-caption">
          <span className="priceband__key priceband__key--outer" />
          del 10 % más barato al 10 % más caro{' '}
          <span className="tabular">
            {format(cell.p10)} – {format(cell.p90)}
          </span>
        </li>
        {intervalo ? (
          <li className="priceband__legend-item t-caption">
            <span className="priceband__key priceband__key--ci" />
            intervalo de confianza de la mediana (95 %){' '}
            <span className="tabular">
              {format(intervalo.lo)} – {format(intervalo.hi)}
            </span>
          </li>
        ) : null}
        {marcas.map((m) => (
          <li key={`leg-${m.tone}-${m.label}`} className="priceband__legend-item t-caption">
            <span
              className={`priceband__key priceband__key--${m.tone === 'anaquel' ? 'caret' : 'ref'}`}
            />
            {m.label} <span className="tabular">{format(m.value)}</span>
          </li>
        ))}
      </ul>

      <div className="chart__foot">
        {/* Los tramos colapsados se declaran. Una marca de 3 px sin explicación
            se lee como un error de dibujo, no como «todas las observaciones
            coincidieron». */}
        {sinDispersion ? (
          <p className="t-caption ink-3 priceband__note">
            Las {integer(cell.n)} observaciones se levantaron al mismo precio,{' '}
            {format(cell.p50)}: no hay nube que dibujar y la banda se reduce a una marca.
          </p>
        ) : null}
        {rangoPuntual ? (
          <p className="t-caption ink-3 priceband__note">
            La mitad central de las {integer(cell.n)} observaciones cayó en un solo precio,{' '}
            {format(cell.p25)}: el rango de negociación se dibuja como marca, no como banda.
          </p>
        ) : null}
        {intervalo === null ? (
          <p className="t-caption ink-3 priceband__note">
            Sin intervalo de confianza para la mediana: {integer(cell.n)} observaciones no
            alcanzan para calcularlo por estadísticos de orden.
          </p>
        ) : null}
        <p className="t-caption ink-3 priceband__note">{footnote}</p>

        <details className="chart__table">
          <summary className="t-footnote">Ver tabla de datos</summary>
          {/* Enfocable a propósito: contenedor con scroll y sin nada enfocable
              dentro. Es el canal de respaldo declarado de la gráfica y tiene que
              ser operable sin ratón (mismo motivo que `LineChart.tsx:680-683`). */}
          <div
            className="chart__table-scroll"
            tabIndex={0}
            role="region"
            aria-label={`Tabla de datos de la banda de precio de ${label}`}
          >
            <table>
              {/* El `caption` nombra el producto, el canal y el mes, como ya
                  hacen `BarList` y `LineChart`: esta tabla es el canal de
                  respaldo y quien la alcanza navegando por tablas no tiene el
                  encabezado de la sección a la vista. Y declara que abajo hay un
                  segundo grupo que no es de esta celda: antes prometía
                  «cuantiles … de la celda» y a continuación venía «Autoservicio
                  $14.40» bajo el mismo encabezado «Estadístico». */}
              <caption className="sr-only">
                {label} · {RETAIL_CHANNEL_LABEL[cell.channel].toLowerCase()} ·{' '}
                {formatPeriodLong(cell.period)}. Cuantiles, intervalo de confianza y cobertura de
                la celda. {unitLabel}.
                {marcas.length > 0
                  ? ' Al final, en su propio grupo, las otras referencias del mismo mes: no son estadísticos de esta celda.'
                  : ''}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Estadístico</th>
                  <th scope="col">Valor</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">10 % más barato (p10)</th>
                  <td className="tabular">{format(cell.p10)}</td>
                </tr>
                <tr>
                  <th scope="row">Inicio del rango de negociación (p25)</th>
                  <td className="tabular">{format(cell.p25)}</td>
                </tr>
                <tr>
                  <th scope="row">Mediana (p50)</th>
                  <td className="tabular">{format(cell.p50)}</td>
                </tr>
                <tr>
                  <th scope="row">Fin del rango de negociación (p75)</th>
                  <td className="tabular">{format(cell.p75)}</td>
                </tr>
                <tr>
                  <th scope="row">10 % más caro (p90)</th>
                  <td className="tabular">{format(cell.p90)}</td>
                </tr>
                <tr>
                  <th scope="row">Intervalo de confianza de la mediana (95 %)</th>
                  {/* Sin intervalo va «—», no un rango inventado a partir de los
                      cuartiles: son dos cosas distintas y confundirlas ensancha
                      la certeza que la celda no tiene. */}
                  <td className="tabular">
                    {intervalo ? `${format(intervalo.lo)} – ${format(intervalo.hi)}` : '—'}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Observaciones</th>
                  <td className="tabular">{integer(cell.n)}</td>
                </tr>
                <tr>
                  <th scope="row">Entidades con observación</th>
                  <td className="tabular">{integer(cell.states)}</td>
                </tr>
                <tr>
                  <th scope="row">Puntos de venta</th>
                  <td className="tabular">{integer(cell.outlets)}</td>
                </tr>
              </tbody>

              {/* Las marcas van en SU grupo, con encabezado que las nombra.
                  En la gráfica el caret naranja y el tick atenuado, con su
                  leyenda, dicen que ese precio es de otro canal o de otra
                  fuente; en la tabla, mezcladas bajo «Estadístico», el $14.40
                  del autoservicio se leía como un cuantil más de la central.
                  `scope="rowgroup"` es lo que ata el encabezado a las filas que
                  siguen dentro del `tbody`, que es exactamente su alcance. */}
              {marcas.length > 0 ? (
                <tbody className="priceband__grupo">
                  <tr>
                    <th scope="rowgroup" colSpan={2}>
                      Otras referencias, mismo mes
                    </th>
                  </tr>
                  {marcas.map((m) => (
                    <tr key={`fila-${m.tone}-${m.label}`}>
                      <th scope="row">{m.label}</th>
                      <td className="tabular">{format(m.value)}</td>
                    </tr>
                  ))}
                </tbody>
              ) : null}
            </table>
          </div>
        </details>
      </div>
    </figure>
  )
}
